/**
 * Main App Layout Route (pathless)
 *
 * Provides the main sidebar for Dashboard, Projects, and Inbox.
 * This is a pathless layout -- URLs are unaffected by the _main segment.
 */

import {
  Image,
  Inline,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Text,
  UserMenu,
} from '@cortex/ui';
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { FolderKanban, Inbox, LayoutDashboard, Shield } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/providers/auth-provider';

export const Route = createFileRoute('/_app/_main')({
  component: MainLayoutComponent,
});

function MainLayoutComponent() {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  };

  return (
    <SidebarProvider className="bg-background h-screen">
      <Sidebar>
        <SidebarHeader>
          <Inline align="center" justify="between">
            <Link to="/" className="flex items-center px-[7%]">
              <Image
                src="/cortex-logo.webp"
                alt="Cortex"
                className="h-auto w-[84%]"
              />
            </Link>
            <SidebarTrigger />
          </Inline>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Main</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname === '/' || pathname === '/_app'}
                    tooltip="Dashboard"
                    onClick={() => navigate({ to: '/' })}
                  >
                    <LayoutDashboard
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Dashboard
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.startsWith('/projects')}
                    tooltip="Projects"
                    onClick={() => navigate({ to: '/projects' })}
                  >
                    <FolderKanban
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Projects
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.startsWith('/inbox')}
                    tooltip="Inbox"
                    onClick={() => navigate({ to: '/inbox' })}
                  >
                    <Inbox className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <Text as="span" truncate className="flex-1 text-left">
                      Inbox
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {isAdmin && (
            <SidebarGroup>
              <SidebarGroupLabel>Admin</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith('/admin')}
                      tooltip="Admin Dashboard"
                      onClick={() => navigate({ to: '/admin' })}
                    >
                      <Shield className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <Text as="span" truncate className="flex-1 text-left">
                        Admin Dashboard
                      </Text>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter>
          {user ? (
            <UserMenu
              user={{
                email: user.email,
                ...(user.name != null && { name: user.name }),
                ...(user.avatarUrl != null && { avatarUrl: user.avatarUrl }),
                ...(user.role != null && { role: user.role }),
                ...(user.organization != null && {
                  organization: user.organization,
                }),
              }}
              collapsed={false}
              {...(isAdmin && {
                onAdminClick: () => navigate({ to: '/admin' }),
              })}
              onSettingsClick={() => navigate({ to: '/settings' })}
              onLogoutClick={handleLogout}
              side="top"
              align="start"
            />
          ) : null}
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
